import type { WidgetImage } from '@elara/domain/models/schemas/widget-schemas';

import { Children, Fragment, isValidElement } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { useFirstAvailableImage } from '#lib/use-first-available-image.js';
import { Badge, getFallbackCoverImageIcon, IconSizes, MaterialIcon } from '#ui';
import { AppImage } from '#ui/AppImage.js';
import { ImageGalleryWidget } from '#ui/chat/ImageGalleryWidget.js';

import { CoverCarousel } from './CoverCarousel.js';

export const BASE_DETAIL_CARD_CONTENT_CLASSES = 'relative flex flex-col gap-1 flex-1 min-w-0 p-3 pb-0';

/**
 * `PreviewLink`'s popover renders the `lg` card as a ROW — photo left, content right — where the chat
 * renders it stacked, title and facts first and the photo strip below. Only direction and order
 * differ, so it is a handful of conditional classes rather than a second layout that would drift
 * from this one.
 *
 * The WIDTH is a proportion: 36% photo, the rest content. A fixed width wide enough for a chat card
 * left the 320px popover no room for the text beside it, and 36% is tuned rather than derived — 33%
 * read as mean, 40% took over the card.
 *
 * `gap-0` because the content column supplies its own padding once it is beside the photo, and the
 * cover drops its aspect ratio to fill the row's height instead of dictating it.
 */
/** How the `lg` card arranges itself — see `DetailCardLayoutProps.layout`. */
export type CardLayout = 'stacked' | 'row';

/**
 * `feature` in chat carousels and the hotel guide's room list, `lg` for a lone recommendation and the
 * link popover, `default` on the shortlist and in message attachments.
 */
export type CardSize = 'default' | 'lg' | 'feature';

// A floor on the row's height: a card with little to say (a destination is a name and a country) otherwise
// squeezed its photo into a letterbox strip.
const ROW_ROOT = 'flex-row gap-0 min-h-22';
const ROW_COVER = 'order-first w-[36%] shrink-0 self-stretch';
// `absolute inset-0`, not `h-full`: a percentage height resolves against the nearest ancestor with a
// definite one, and `LinkWrap`'s anchor sits in between with an auto height — so `h-full` computed to
// nothing and the cover collapsed, taking the photo with it. Positioning against the wrapper (which
// IS `relative`, and stretches to the row) skips the anchor entirely.
const ROW_COVER_IMAGE = 'absolute inset-0 aspect-auto rounded-none';
const ROW_BODY = 'min-w-0 flex-1 p-3';

/**
 * The `feature` card's cover band. A FIXED 6:4 rather than each photo's natural ratio, so every card
 * in a carousel is the same height and a placeholder can reserve the identical box — the row no
 * longer reflows as images arrive. Exported so the loading and failure placeholders reserve exactly this box.
 */
export const FEATURE_COVER_CLASSES = 'aspect-[3/2] w-full rounded-md';

/**
 * The cover's action corner. Each action gets its OWN circle rather than the row sharing one pill: two
 * icons in a single capsule read as one segmented control — press either half of the same thing —
 * where they are in fact unrelated (shortlist, add to trip). Separate circles say two buttons.
 *
 * The frosting is what makes a bare icon legible over a photograph, white on a pale sky and dark on a
 * night shot alike, without the solid chip reading as a floating button.
 *
 * `Children.toArray` flattens arrays and drops nullish entries, so a caller can pass one action or
 * several. It does NOT flatten a fragment — a caller with several passes an array.
 */
/**
 * The "Sold out" pill, centred on the cover band it is given the bounds of. Rendered as a sibling of
 * the dimmed cover rather than inside it, so the badge itself stays at full contrast — a dimmed
 * "Sold out" is the one thing on the card that must not be hard to read.
 */
/**
 * Wraps a cover in the card's own link when there is one, and leaves it alone when there is not.
 *
 * Exists because "no link" is a real state here — a card with no destination, or one whose entity has
 * sold out — and an `href`-less anchor is neither focusable nor exposed as a link, so it cannot just
 * always be rendered.
 */
function LinkWrap({
  canLink,
  href,
  onClick,
  children,
}: {
  canLink: boolean;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}): React.ReactNode {
  if (!canLink) return children;
  return (
    // `min-w-0`, or this anchor refuses to shrink below its content's min-content width as a flex
    // item, and a long title shoves the card's actions off the side of the screen instead of
    // truncating. The `truncate` inside it cannot fire until an ancestor allows the shrink.
    <a href={href} onClick={onClick} className="block min-w-0 no-underline text-inherit">
      {children}
    </a>
  );
}

function SoldOutBadge(): React.ReactNode {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-1">
      <Badge variant="destructive" size="lg">
        Sold out
      </Badge>
    </div>
  );
}

/**
 * Flattens one level of fragment, so a caller passing `<><A/><B/></>` gets two actions rather than one.
 *
 * `Children.toArray` flattens ARRAYS but counts a fragment as a single child, which is easy to miss:
 * the callers that passed a bare element or an array got one frosted circle each, while the first one
 * to pass a fragment got both of its buttons sealed inside the same pill. Asking every caller to
 * remember an array — and to key it — is the kind of rule that only holds until someone forgets.
 */
function flattenActions(children: React.ReactNode): React.ReactNode[] {
  return Children.toArray(children).flatMap((child) =>
    isValidElement(child) && child.type === Fragment
      ? Children.toArray((child.props as { children?: React.ReactNode }).children)
      : [child],
  );
}

function CoverActions({ children }: { children: React.ReactNode }): React.ReactNode {
  return (
    <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
      {flattenActions(children).map((action, index) => (
        <div key={index} className="flex items-center rounded-full bg-background/70 p-0.5 shadow-sm backdrop-blur-md">
          {action}
        </div>
      ))}
    </div>
  );
}

export function CoverImage({
  imageUrls,
  title,
  type,
  size = 'default',
  className,
  onClick,
}: {
  /**
   * The entity's photos, best first, tried in order when earlier ones fail to load. Supplier photo
   * CDNs rot — a third of the URLs on some stored rooms 404 — and a card that only ever tries
   * `images[0]` shows a placeholder for an entity that has perfectly good photos at index 1. That was
   * exactly the difference between a room in the chat carousel (which pages, so it skips the dead
   * ones) and the same room in the shortlist.
   */
  imageUrls?: string[];
  title?: string;
  type: string;
  size?: CardSize;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const { src: currentUrl, onError } = useFirstAvailableImage(imageUrls ?? []);
  const icon = getFallbackCoverImageIcon(type);
  const imageSize = size === 'lg' || size === 'feature' ? 'w-full aspect-video rounded-none' : 'w-22';

  if (currentUrl) {
    return (
      <div
        className={cn(
          'relative flex items-center justify-center flex-shrink-0 overflow-hidden bg-background-quaternary',
          imageSize,
          className,
          onClick && 'cursor-pointer',
        )}
        onClick={onClick}
      >
        {/* Two layers rather than a z-index: the photo is a LATER sibling, so it paints over the icon,
            and a URL that 404s simply fails to cover it. The icon cannot be a child of the element
            carrying the background — background-colour and background-image paint in one step, so
            nothing can sit between them — and it cannot use a negative z-index either, which would put
            it behind that element's own background and hide it permanently.

            An error handler is not an option here the way it is in `CoverCarousel`: a CSS background
            fires no load or error event, so the fallback has to be something the failure uncovers. */}
        <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <MaterialIcon icon={icon} size={IconSizes.md} className="text-muted-foreground/40" weight="light" />
        </div>
        {/* An `<img>`, not a CSS background: a background fires no error event, so a dead URL could only
            ever be uncovered, never stepped past. `key` forces a fresh element per candidate so the
            browser actually re-requests rather than reusing the failed one. */}
        <AppImage
          key={currentUrl}
          src={currentUrl}
          alt={title ?? ''}
          // Steps to the next photo only once this one's original has failed too, not its rendition.
          onAllFailed={onError}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    );
  }

  // `quaternary`, not `secondary`: the chat feed's own background IS `background-secondary`, so a band
  // painted in it has no edges — an entity with no photo read as a hole in the card rather than as a
  // placeholder. `CoverCarousel` paints the same surface for the same reason, and the two sit side by
  // side in a carousel, where any difference between them would show.
  return (
    <div
      className={cn(
        'flex items-center justify-center flex-shrink-0 overflow-hidden bg-background-quaternary',
        imageSize,
        className,
        onClick && 'cursor-pointer',
      )}
      onClick={onClick}
    >
      <MaterialIcon icon={icon} size={IconSizes.md} className="text-muted-foreground/40" weight="light" />
    </div>
  );
}

/**
 * An entity's summary line under its facts — the same two clamped lines on the hotel, place and
 * destination cards, which is why it lives here rather than three times over.
 *
 * NOT on `lg`: that card leads with a title, the facts and the price, and a paragraph under them
 * pushed the photos below the fold. The carousel and shortlist sizes keep it — there the two lines are
 * the only thing distinguishing one card from the next.
 */
export function CardDescription({ children, size }: { children?: string | null; size: CardSize }): React.ReactNode {
  if (!children || size === 'lg') return;
  return <p className="text-xs line-clamp-2">{children}</p>;
}

export interface BaseDetailCardLayoutProps {
  size?: CardSize;
  /**
   * The thing this card describes can no longer be had — a sold-out room or rate. Dims the cover and
   * the content and centres a "Sold out" badge over the cover band, matching
   * `mobile/src/ui/cards/DetailCardLayout.tsx`. `lg` and `feature` only, for the same reason mobile
   * restricts it to `large`: this component owns the band's exact bounds, so the badge can anchor to
   * it rather than a caller guessing its height.
   *
   * `actions` stay live and undimmed — a shortlisted room should still be removable once it sells out
   * — and `footerActions` are left alone, visible but expected to disable themselves. Linking is
   * suppressed, as with a card that has nowhere to go, unless `linkWhenUnavailable` says otherwise.
   */
  unavailable?: boolean;
  /**
   * Keep the card's link while it is `unavailable`. A room card sets it: its guide is where the other
   * parties' rates and the full picture live, so a sold-out-for-this-party room must still open it.
   * Opening the guide commits to nothing; only the book and add actions depend on availability.
   */
  linkWhenUnavailable?: boolean;
  actions?: React.ReactNode;
  className?: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  displayPricing?: boolean;
  showTotals?: boolean;
  icon?: React.ReactNode;
}

export interface DetailCardLayoutProps extends BaseDetailCardLayoutProps {
  /**
   * Content beside the title on the same line — a short qualifier belonging TO
   * the name rather than sitting under it (a destination's country, say). It never shrinks: the TITLE
   * truncates first, because a clipped country beside an intact name reads as a bug, while a clipped
   * long name beside an intact country reads as a long name.
   */
  titleAside?: React.ReactNode;
  images?: WidgetImage[];
  title: string;
  type: string;
  children: React.ReactNode;
  /**
   * A row of labelled actions at the bottom of the content column (`lg` only) — "Add to trip",
   * "Details", "Book now".
   * The `actions` corner above stays reserved for the icon-only `ShortlistButton`, the one action
   * every card type gets; a bare icon doesn't read as "book this room" the way a labelled button
   * does. Mirrors mobile's `DetailCardLayout`.
   */
  footerActions?: React.ReactNode;
  /**
   * Whether the `lg` cover pages through every image (default) or shows only the first. Transient
   * hover previews pass `false`: opening the lightbox from inside a `HoverCard` would unmount the
   * dialog along with the popover the moment the pointer left it, and a carousel is noise in a
   * glanceable preview besides.
   */
  /**
   * How the `lg` card arranges itself. `'stacked'` — the chat card — leads with the title and facts
   * and puts the photos in a scrollable strip below them. `'row'` puts a single cover photo on the
   * left with the content beside it, which is what `PreviewLink`'s 320px hover popover needs: a strip
   * cannot live in a 36% column, and a glance has no room for the stacked card's height.
   *
   * This used to be `showCoverGallery`, a name from when the popover's reason was avoiding a lightbox
   * that unmounted with the hover card. Cover photos open the guide now, so that hazard is gone and
   * what remains is a layout choice — which is also what drives the classes below, so the popover
   * states it once instead of passing a flag AND marking itself with a CSS class.
   */
  layout?: CardLayout;
  /** Extra classes for the title (`feature` only) — a surface that lists fewer, larger cards. */
  titleClassName?: string;
  /**
   * A badge over the cover's top-left corner (`feature` only), opposite the actions, e.g. what kind of
   * pick a room is. Clicks pass through it to the photo.
   */
  coverBadge?: React.ReactNode;
}

export function DetailCardLayout({
  images,
  title,
  type,
  size = 'default',
  children,
  actions,
  footerActions,
  layout = 'stacked',
  icon,
  titleAside,
  href,
  onClick,
  unavailable = false,
  linkWhenUnavailable = false,
  className,
  titleClassName,
  coverBadge,
}: DetailCardLayoutProps) {
  // Every photo, best first, so a dead first URL falls through to a live one rather than to the placeholder.
  const imageUrls = images?.map((image) => image.url);
  // A sold-out card goes nowhere — the guide it would open describes something that cannot be had —
  // unless the caller says its guide still has something to show (a room's other parties and rates).
  const linkSuppressed = unavailable && !linkWhenUnavailable;
  const canLink = (!!href || !!onClick) && !linkSuppressed;
  const linkHref = linkSuppressed ? undefined : href;
  const linkOnClick = linkSuppressed ? undefined : onClick;
  // The cover and the content dim; the actions corner and the footer row do not. A shortlisted room
  // must stay removable once it sells out, and the footer buttons disable themselves.
  const dimmed = unavailable ? 'opacity-50' : undefined;
  // Only `lg` gets the gallery: `default` is a list row, where paging through photos would be noise.
  // `feature` keeps its gallery in either layout: its row puts the same 6:4 cover beside the content.
  const coverImages =
    (size === 'feature' || (size === 'lg' && layout === 'stacked')) && images?.length ? images : undefined;
  // `footerActions` is a `lg`-only affordance; the other sizes have no room for a button row. It sits
  // at the bottom of the content column (`mt-auto`) rather than spanning the card, so the buttons line
  // up with the text they belong to instead of running under the cover photo. It stays a sibling of the
  // link overlay, never inside it — a button nested in the card's `<a>` would navigate as well as fire.
  const hasFooter = (size === 'lg' || size === 'feature') && !!footerActions;
  // The whole card opens the guide, EXCEPT where the click landed on something interactive of its
  // own — a gallery photo opening the lightbox, an action button, the title's own link. Decided by
  // walking up from the click target rather than asking every child to stop propagation: children
  // come and go, and one that forgot would silently navigate instead of doing its job.
  //
  // This is what earns the hover halo. A card that lifts under the pointer promises that the whole
  // surface is one button, and before this only the title and the cover answered.
  // The action rows say so outright rather than leaving it to the guard below. A button that
  // re-renders on click — which most of these do, swapping "Add to trip" for a stepper — can leave
  // the guard walking up from a node React has already detached, and the card would navigate out
  // from under the thing the person actually pressed.
  const stopCardSurface = (event: React.MouseEvent) => event.stopPropagation();

  const openFromCardSurface = (event: React.MouseEvent) => {
    if (!linkOnClick) return;
    const root = event.currentTarget as HTMLElement;
    const target = event.target as HTMLElement;
    // Not our surface at all. A React PORTAL bubbles through the React tree rather than the DOM one,
    // and this card contains several: the gallery's lightbox, the itinerary button's dialogs. Every
    // click inside an open dialog was arriving here — so dismissing a lightbox opened the guide
    // behind it. The DOM containment check is what tells the card's own surface from a portal's.
    if (!root.contains(target)) return;
    if (target.closest('a,button,[role="button"],input,select,textarea')) return;
    linkOnClick(event);
  };

  /**
   * `feature` — the carousel card: cover on TOP, content beneath, shortlist button overlaid on the
   * photo's top-right corner.
   *
   * This is the stacked shape `lg` deliberately abandoned, and it is right here for the reason it was
   * wrong there. A stacked card spanning the full message turned one recommendation into a ~380px-tall
   * photo; in a carousel the card is a fraction of that width, so the same proportions produce a
   * modest image and several entities fit on screen at once — which is the whole point of the row.
   * `lg` keeps the side-by-side treatment for a lone recommendation.
   *
   * The actions corner moves ONTO the photo rather than sitting above the text: with cards this narrow
   * a heart in the content column steals a line of title, and the image is the only place with room to
   * spare. It sits outside the link overlay so shortlisting never navigates.
   */
  if (size === 'feature') {
    // `linkHref`/`linkOnClick`, not the raw props: they are the ones an `unavailable` card clears, and
    // the body below already uses them — sending the photo somewhere the text beneath it refuses to go
    // is the kind of split a sold-out card should not have.
    const cover = coverImages ? (
      // The gallery carries its own link rather than being wrapped in one: a click on a photo goes
      // where the card goes, but a click on a paging arrow must not, and the arrows live inside it.
      <CoverCarousel
        images={coverImages}
        title={title}
        type={type}
        className={FEATURE_COVER_CLASSES}
        href={linkHref}
        onClick={linkOnClick}
      />
    ) : (
      // A card with NO photo still has a cover — the type's icon on a tinted band — and it has to go
      // where the rest of the card goes. Unwrapped, it was the one dead patch on an otherwise
      // clickable card, and on an entity with no imagery that patch is most of what you see.
      <LinkWrap canLink={canLink} href={linkHref} onClick={linkOnClick}>
        <CoverImage imageUrls={imageUrls} title={title} type={type} size={size} className={FEATURE_COVER_CLASSES} />
      </LinkWrap>
    );

    const body = (
      <div className={cn(BASE_DETAIL_CARD_CONTENT_CLASSES, 'gap-1.5 px-0 pt-4', hasFooter && 'pb-0')}>
        {/* The body face at `text-base`, not the display face: under a cover photo a name only has to
            say which room or place this is, and the display size outweighed the photo above it.
            `leading-tight` keeps the title attached to the facts under it; `gap-2` then sets the actual
            spacing, instead of it being whatever the font's metrics happened to leave behind. */}
        <div className="flex items-baseline gap-2">
          {icon}
          <h4 className={cn('font-sans text-base font-medium truncate min-w-0 m-0', titleClassName, 'leading-tight')}>
            {title}
          </h4>
          {titleAside && <span className="shrink-0">{titleAside}</span>}
        </div>
        {children}
      </div>
    );

    const content = (
      <>
        {canLink ? (
          <a href={linkHref} onClick={linkOnClick} className="block no-underline text-inherit">
            {body}
          </a>
        ) : (
          <div className={dimmed}>{body}</div>
        )}
        {/* `pt-3`, not the content's own `gap`: the body above ends on a price or a fact, and 4px left
            the buttons reading as one more line of it rather than as the row you act from. */}
        {hasFooter && (
          <div className="flex flex-wrap items-center gap-2 pt-3" onClick={stopCardSurface}>
            {footerActions}
          </div>
        )}
      </>
    );

    return (
      // The surface between the parts answers too: the gaps around the text, and a hover halo that
      // extends past the card itself (the chat carousel's, the hotel guide's room grid).
      <div className={cn('flex flex-col', canLink && 'cursor-pointer', className)} onClick={openFromCardSurface}>
        {/* `isolate` keeps the overlays' `z-10` inside the card, so they slide under a sticky header
            (the guide's) when the card scrolls beneath it instead of painting over it. */}
        <div className="relative isolate">
          <div className={dimmed}>{cover}</div>
          {unavailable && <SoldOutBadge />}
          {coverBadge && <div className="pointer-events-none absolute top-2 left-2 z-10 flex">{coverBadge}</div>}
          {actions && <CoverActions>{actions}</CoverActions>}
        </div>
        {content}
      </div>
    );
  }

  /**
   * `lg` — a LONE recommendation, restored to the shape it had before the card rework.
   *
   * The side-by-side version (portrait cover left, content right) is still right for `feature`, where
   * a card is a third of the message width. At full width it cost the two things a single
   * recommendation leans on hardest: the title shrank to a card heading, and one 36% column can only
   * ever show ONE photo, so a hotel with twelve of them looked like a hotel with one. Here the title
   * leads at `text-xl` and the photos run as a scrollable strip beneath the facts.
   *
   * What the rework added is kept: the footer action row, and the shortlist action beside the title.
   */
  if (size === 'lg') {
    const isRow = layout === 'row';
    // A stacked card with no photos drops the band altogether. The popover's row keeps its placeholder
    // — there the cover is the left column, and without it the content would fill a card sized for two.
    // A full-width stacked band of nothing but a bed icon was most of the card's height saying nothing.
    const hasCover = !!coverImages || isRow;

    const cover = coverImages ? (
      // The real gallery, not a single cover: several photos, horizontally scrollable, with its own
      // arrows and a count. `ImageGalleryWidget` already owns that behaviour, so the card asks for it
      // rather than growing a second implementation — but NOT its lightbox: a photo here is a picture
      // of the thing the card is about, so tapping one goes where the rest of the card goes. The
      // guide carries the full gallery anyway, and `CoverCarousel` made the same call for the
      // carousel card's band.
      <ImageGalleryWidget
        images={coverImages}
        layout="inline"
        displayCount={coverImages.length > 5}
        onImageClick={linkOnClick}
      />
    ) : (
      <LinkWrap canLink={canLink} href={linkHref} onClick={linkOnClick}>
        <CoverImage
          imageUrls={imageUrls}
          title={title}
          type={type}
          size={size}
          className={cn('w-full aspect-video rounded-md', isRow && ROW_COVER_IMAGE)}
        />
      </LinkWrap>
    );

    return (
      // `layout="row"` turns this stacked card into a ROW — photo left, content right. `PreviewLink`'s
      // popover is 320px wide and a glance: stacked, the photo pushed the facts below the fold, and it
      // is the shape that popover has always had. Done in CSS rather than a second branch because the
      // two differ only in direction and order, and a duplicated layout is a layout that drifts.
      <div
        className={cn('flex flex-col gap-3', isRow && ROW_ROOT, canLink && 'cursor-pointer', className)}
        onClick={openFromCardSurface}
      >
        <div className={cn('flex items-start justify-between gap-4', isRow && ROW_BODY)}>
          <LinkWrap canLink={canLink} href={linkHref} onClick={linkOnClick}>
            <div className={cn('flex flex-col gap-1 min-w-0', dimmed)}>
              <div className="flex items-center gap-2 min-w-0">
                {icon}
                {/* `h3`, because this is the heading of a recommendation rather than the label of a
                    card in a row — but at `text-lg`, not the `text-xl` it once used: with the facts
                    and the price now sitting right under it, `xl` was shouting over them. The popover's
                    row takes the shortlist card's title (small, medium, sans), and wraps rather than
                    truncating: a popover is for reading which place this is, and "The Carlyle A …"
                    does not say. */}
                <h3
                  className={cn(
                    'font-medium m-0 min-w-0',
                    isRow ? 'font-sans text-sm text-pretty' : 'font-display text-lg truncate',
                  )}
                >
                  {title}
                </h3>
                {titleAside && <span className="shrink-0">{titleAside}</span>}
              </div>
              {children}
            </div>
          </LinkWrap>
          {/* The corner takes the ICON actions — the shortlist heart, and anything else a card can say
              in a glyph. Beside the title rather than over a photo: with the band below the text there
              is no corner on the image to sit in. */}
          {/* Barely a gap: these are icon buttons whose 40px targets are mostly padding, so a `gap-2`
              between them read as two unrelated controls rather than one cluster. */}
          {/* The popover's row puts them on its photo instead (below), where a narrow card has room. */}
          {actions && !isRow && (
            <div className="flex shrink-0 items-center gap-0.5" onClick={stopCardSurface}>
              {actions}
            </div>
          )}
        </div>

        {hasCover ? (
          <div className={cn('relative', isRow && ROW_COVER)}>
            <div className={dimmed}>{cover}</div>
            {/* Not in the popover's row: there the card's own content says "Sold out" beside the price,
                and the photo column is too narrow to carry a second one. */}
            {unavailable && !isRow && <SoldOutBadge />}
            {/* In the popover's row the icon actions sit over the photo's top right corner, as on the
                carousel's cards: beside the title they took most of a 320px card's line. Outside the
                dimmed photo, so a shortlisted room that sold out can still be removed. */}
            {actions && isRow && <CoverActions>{actions}</CoverActions>}
          </div>
        ) : (
          // With no band to centre it on, "Sold out" takes the band's place instead of disappearing.
          unavailable && (
            <div>
              <Badge variant="destructive" size="lg">
                Sold out
              </Badge>
            </div>
          )
        )}

        {/* Labelled buttons go BELOW the gallery, not in the corner. A room's "Add to trip" and "Book
            now" are the point of the card and need their words; shrunk to glyphs beside the heart they
            read as incidental, and "book" is not a thing to guess at from an icon. */}
        {hasFooter && (
          <div className="flex flex-wrap items-center gap-2" onClick={stopCardSurface}>
            {footerActions}
          </div>
        )}
      </div>
    );
  }

  // Default layout
  let cardBody = (
    <div className={cn('flex', className)}>
      {/* The compact row dims but draws NO badge over its cover — at ~88px wide there is no room for
          one that stays legible. The card's own content carries it instead, beside the price (see
          `RoomCard`), which is where the compact flight card puts it too. */}
      <div className={cn('flex shrink-0', dimmed)}>
        <CoverImage imageUrls={imageUrls} title={title} type={type} size={size} />
      </div>
      {/* Shortlist cards get extra room beneath the content. The cover runs full-bleed down the left
          edge with no padding of its own, so the content column's even `p-3` reads tight at the bottom
          against it — the description's last line sits all but on the card's edge. Scoped to
          `.sl-card` so the other `default`-size consumers (preview rows, pickers) are unaffected. */}
      <div className="relative flex flex-1 min-w-0">
        <div className={cn(BASE_DETAIL_CARD_CONTENT_CLASSES, '[.sl-card_&]:pb-3')}>
          {/* The dim goes on the TITLE, not the whole column: a card's `children` may carry a "Sold
              out" badge of its own (see `RoomCard`), and opacity applies to a subtree — a child cannot
              be brighter than its parent. A card with an unavailable state dims its own content and
              leaves its badge alone. */}
          <div className={cn('flex items-baseline gap-2', 'pr-8 [.sl-card_&]:pr-16', dimmed)}>
            {icon}
            <h4 className="font-medium font-sans text-sm truncate min-w-0">{title}</h4>
            {titleAside && <span className="shrink-0">{titleAside}</span>}
          </div>

          {children}
        </div>

        {/* Outside the dim: removing a sold-out item is the one thing still worth doing with it, so its
            control must not read as disabled. Matches the flight card. */}
        {!canLink && actions && <div className="absolute top-1 right-1 z-10">{actions}</div>}
      </div>
    </div>
  );

  if (canLink) {
    return (
      <div className="relative w-full">
        <a href={href} onClick={onClick} className="block no-underline text-inherit">
          {cardBody}
        </a>
        {actions && <div className={cn('absolute top-1 right-1 z-10')}>{actions}</div>}
      </div>
    );
  }

  return cardBody;
}
